/**
 * Ob das Anlagenschema einer Vorlage ein Kästchen „Verbrauch im Haus" braucht.
 *
 * Es steht für den Verbrauch, den **kein Abzweig zählt**: den Rest hinter dem
 * Zähler am Hausanschluss. Bei den Einfamilienhaus-Vorlagen gibt es ihn immer —
 * „Nur Netzbezug" hat gar keinen Abzweig, und beim Hauptzähler mit Unterzählern
 * ist der Allgemeinstrom genau dieser Rest. Bei einer Vorlage, die **je
 * Wohneinheit einen Steckplatz** führt (`layout.perUnit`), sitzt dagegen jeder
 * Verbraucher hinter einem eigenen Zähler; der Hausverbrauch ist die Summe der
 * Unterzähler, und ein Rest aus dem Summenzähler wäre eine Zahl, die niemand
 * gemessen hat (`tenant-power-graph.ts`). Dort gibt es das Kästchen nicht.
 *
 * Entschieden wird über das, was die Vorlage über sich sagt, nicht über ihren
 * Schlüssel: Eine dritte Mieterstrom-Vorlage soll die Regel erben, ohne dass
 * jemand hier eine Liste nachzieht.
 */
export function hasUnmeteredRest(template: { readonly layout: { readonly perUnit: boolean } | null }): boolean {
  return template.layout === null || !template.layout.perUnit;
}

/**
 * Ob die Verbraucher einer Vorlage **hinter „Verbrauch im Haus"** hängen
 * (HM-110, HM-114) — als Aufschlüsselung und nicht als weiterer Abzweig.
 *
 * In einer Hausvorlage zählt der Hauptzähler das ganze Haus; eine Wallbox mit
 * eigenem Zähler ist ein **Teil** davon, und das Konzept rechnet den übrigen
 * Verbrauch als `max(Hausverbrauch − Σ Verbraucher, 0)`. Neben „Verbrauch im
 * Haus" an die Hausverteilung gezeichnet, sähe sie aus wie ein zweiter Summand —
 * genau die Doppelzählung, die die Rechnung vermeidet. Im Mieterstrom gibt es
 * dagegen keinen ungemessenen Rest, und ein Verbraucher des Hauses ist dort ein
 * Summand; er bleibt ein Abzweig.
 *
 * Eine Vorlage ohne Layout (eine Fassung aus der Zeit vor HM-110) kennt keine
 * Verbraucher und bekommt keinen Kasten, der etwas anbietet, das sie nicht kann.
 */
export function consumersBehindHouse(template: {
  readonly layout: { readonly perUnit: boolean; readonly consumers: boolean } | null;
}): boolean {
  return template.layout !== null && !template.layout.perUnit && template.layout.consumers;
}
